import {
  ResponseTypeEnum,
  TemplateNodeTypeEnum,
  TraceKey,
  TraceKeyResponseTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { TemplateNodesDict } from 'src/exShared/types/types'
import {
  countTraceKey,
  MAX_TRACE_KEYS_PER_TEMPLATE,
} from 'src/exShared/util/nodes/templateNodeTraceKey'
import { err, ok, ValidationResult } from './types'
import { getSectionChildNodes } from './utils'

type TraceKeyValidatorConfig = {
  questionResponseType: ResponseTypeEnum
  traceKeyResponseType: TraceKeyResponseTypeEnum
  errorMessage: (questionName: string) => string
}

const createTraceKeyValidator =
  ({
    questionResponseType,
    traceKeyResponseType,
    errorMessage,
  }: TraceKeyValidatorConfig) =>
  (
    templateNodes: TemplateNodesDict,
    traceKeys: TraceKey[],
  ): ValidationResult => {
    const nodes = Object.values(templateNodes)

    for (const node of nodes) {
      if (node.type !== TemplateNodeTypeEnum.Question) {
        continue
      }
      if (node.question?.responseType !== questionResponseType) {
        continue
      }
      const traceKeyId = node.traceKey?.traceKeyId
      if (!traceKeyId) {
        continue
      }
      const traceKey = traceKeys.find(tk => tk.id === traceKeyId)
      if (!traceKey) {
        continue
      }
      if (traceKey.responseType !== traceKeyResponseType) {
        return err({
          type: 'page',
          message: errorMessage(node.question?.name || '（レポート名未設定）'),
        })
      }
    }
    return ok()
  }

/**
 * セット選択肢の質問に、セット選択肢以外の検索キーが付与されていないかチェック
 *
 * @param templateNodes すべてのテンプレートノード
 * @param traceKeys すべての検索キー
 * @returns バリデーション結果
 */
export const validateResponseSetTraceKey = createTraceKeyValidator({
  questionResponseType: ResponseTypeEnum.RESPONSE_SET,
  traceKeyResponseType: TraceKeyResponseTypeEnum.ResponseSet,
  errorMessage: questionName =>
    `「${questionName}」には、セット選択肢の検索キーのみ設定できます。`,
})

/**
 * 日時の質問に、日時以外の検索キーが付与されていないかチェック
 *
 * @param templateNodes すべてのテンプレートノード
 * @param traceKeys すべての検索キー
 * @returns バリデーション結果
 */
export const validateDateTimeTraceKey = createTraceKeyValidator({
  questionResponseType: ResponseTypeEnum.DATETIME,
  traceKeyResponseType: TraceKeyResponseTypeEnum.Date,
  errorMessage: questionName =>
    `「${questionName}」には、日時の検索キーのみ設定できます。`,
})

/**
 * テキストの質問に、テキスト以外の検索キーが付与されていないかチェック
 *
 * @param templateNodes すべてのテンプレートノード
 * @param traceKeys すべての検索キー
 * @returns エラーメッセージ。問題なければ空文字
 */
export const validateTextTraceKey = createTraceKeyValidator({
  questionResponseType: ResponseTypeEnum.TEXT,
  traceKeyResponseType: TraceKeyResponseTypeEnum.Text,
  errorMessage: questionName =>
    `「${questionName}」には、テキストの検索キーのみ設定できます。`,
})

/**
 * 繰り返しセクション内に検索キーが設定されていないかチェック
 *
 * @param templateNodes すべてのテンプレートノード
 * @returns バリデーション結果
 */
export const validateRepeatSectionTraceKey = (
  templateNodes: TemplateNodesDict,
): ValidationResult => {
  const nodes = Object.values(templateNodes)

  for (const node of nodes) {
    // セクションノードのみチェック
    if (node.type !== TemplateNodeTypeEnum.Section) {
      continue
    }

    // 繰り返しセクションのみチェック
    if (node.section?.isRepeat !== 1) {
      continue
    }

    // 繰り返しセクション配下の全ノードを取得
    const childNodes = getSectionChildNodes(node, templateNodes)

    // 配下の質問ノードに検索キーが設定されているかチェック
    for (const childNode of childNodes) {
      if (childNode.type !== TemplateNodeTypeEnum.Question) {
        continue
      }

      const traceKeyId = childNode.traceKey?.traceKeyId
      if (traceKeyId) {
        return err({
          type: 'page',
          message: `
        「このセクションを繰り返す」または「従業員チェック」セクション内の質問に、検索キーを設定することはできません。\n
        「${node.section?.name || '（セクション名未設定）'}」内の「${childNode.question?.name}」に検索キーが設定されています。\n
        質問をセクション外にするか、検索キーの紐付けを外してください。
        `,
        })
      }
    }
  }

  return ok()
}

/**
 * ひな形内で検索キーが設定されている質問の数が最大数を超えていないかチェック
 *
 * @param templateNodes すべてのテンプレートノード
 * @returns バリデーション結果
 */
export const validateTraceKeyCount = (
  templateNodes: TemplateNodesDict,
): ValidationResult => {
  const usedCount = countTraceKey(templateNodes)
  if (usedCount > MAX_TRACE_KEYS_PER_TEMPLATE) {
    return err({
      type: 'page',
      message: `このひな形で検索キーを設定できる質問は最大${MAX_TRACE_KEYS_PER_TEMPLATE}件までです。現在${usedCount}件の質問に検索キーが設定されています。`,
    })
  }

  return ok()
}
